import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import OtpInput from 'components/UI/Inputs/OtpInput'
import CountdownTimer from 'components/UI/Timer'
import Typography from 'components/UI/Typography'
import { Fragment, ReactNode } from 'react'
import { Control, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import tw, { type TwStyle } from 'twin.macro'

type OtpVerificationVariants = 'phone-otp' | 'dd-otp' | 'certification-otp'

type OtpVerificationProps<T extends FieldValues> = {
  variant?: OtpVerificationVariants
  phone?: string
  control: Control<T>
  name: Path<T>
  errorMsg?: string
  disabled?: boolean
  otpLength?: number
  initMinutes?: number
  initSeconds?: number
  showOtp?: boolean
  showPhoneChange?: boolean
  footerContent?: ReactNode
  twStyle?: TwStyle
  onOtpVerify?: (value: string | undefined) => void
  onChangePhone?: () => void
  onOtpTimerReset?: () => void
}

/* 
  TODO:Убрать проп variant и оставить только ввод OTP
  Вынести показ текста в разные компоненты так как щас невозможно извне менять содержимое этого компонента
*/
const OtpVerification = <T extends FieldValues>({
  variant = 'phone-otp',
  phone,
  control,
  name,
  errorMsg,
  disabled = false,
  otpLength = 4,
  initMinutes = 0,
  initSeconds = 59,
  showOtp = true,
  showPhoneChange = true,
  footerContent,
  twStyle,
  onChangePhone,
  onOtpVerify,
  onOtpTimerReset
}: OtpVerificationProps<T>) => {
  const handleOtpVerify = (value: string | undefined) => {
    onOtpVerify?.(value)
  }

  const { t } = useTranslation()

  return (
    <InfoBlock twStyle={[tw`space-y-4 mt-[10px] md:mt-0`, twStyle]}>
      {variant === 'phone-otp' ? (
        <Fragment>
          <Typography variant='body' weight='bold'>
            {t('flow-sended-code')}
          </Typography>
          <div tw='space-y-1'>
            <Typography variant='subbody' twStyle={tw`text-grey-dark`}>
              {t('flow-sended-code2')}
            </Typography>
            <Typography variant='subbody' weight='bold'>
              {phone}
            </Typography>
            {showPhoneChange && (
              <Button variant='link' onClick={onChangePhone}>
                {t('flow-change-phone')}
              </Button>
            )}
          </div>
        </Fragment>
      ) : variant === 'certification-otp' ? (
        <Fragment>
          <Typography variant='body' weight='bold'>
            {t('flow-sended-code-certification')}
          </Typography>
          <div tw='space-y-1'>
            <Typography variant='subbody' twStyle={tw`text-grey-dark`}>
              {t('flow-sended-code2')}
            </Typography>
            <Typography variant='subbody' weight='bold'>
              {phone}
            </Typography>
            {showPhoneChange && (
              <Button variant='link' onClick={onChangePhone}>
                {t('flow-change-phone')}
              </Button>
            )}
          </div>
        </Fragment>
      ) : variant === 'dd-otp' ? (
        <Fragment>
          <Typography variant='body' weight='bold'>
            {t('flow-digital-doc')}
          </Typography>
          <Typography variant='subbody' text twStyle={tw`text-grey-dark`}>
            {t('flow-confirm-sms')}
          </Typography>
        </Fragment>
      ) : null}
      {showOtp && (
        <Fragment>
          <OtpInput
            t={t}
            numInputs={otpLength}
            label={t('flow-enter-code') as string}
            errorMsg={errorMsg}
            control={control}
            name={name}
            shouldAutoFocus
            disabled={disabled}
            onVerify={handleOtpVerify}
          />
          <CountdownTimer initialMinutes={initMinutes} initialSeconds={initSeconds} onResetTimer={onOtpTimerReset} />
        </Fragment>
      )}
      {footerContent}
    </InfoBlock>
  )
}

export default OtpVerification
